/*
 * The operations timeline, with `log.md` beside it.
 *
 * Two views of the same events on purpose. The left column is the platform's record
 * — what ran, when, what it changed. The right is the agent's own narrative log, a
 * plain markdown file it appends to with a fixed `## [YYYY-MM-DD] op | Title`
 * prefix. Seeing them together is how you notice the interesting case: an operation
 * the platform recorded that the agent did not write down, or the reverse.
 */
import {
  getWikiContent,
  WIKI_LOG_PATH,
  type History,
  type HistoryOperation,
  type WikiContent,
} from '../../../api/knowledgeBases.ts'
import { Empty, Loaded } from '../components/AsyncState.tsx'
import { byteDelta, dateTime, duration, num } from '../lib/format.ts'
import { authorLabel, opLook, statusLook } from '../lib/operations.ts'
import { useResource, type Resource } from '../lib/useResource.ts'

interface ActivityTabProps {
  kbId: string
  history: Resource<History>
}

export function ActivityTab({ kbId, history }: ActivityTabProps) {
  const log = useResource<WikiContent>(`${kbId}:log`, () => getWikiContent(kbId, WIKI_LOG_PATH))

  return (
    <div className="pane">
      <div className="two-col">
        <div>
          <span className="kicker" style={{ display: 'block', marginBottom: 14 }}>
            Операции
          </span>
          <Loaded resource={history} what="операции" skeletonRows={6}>
            {(data) =>
              (data.ops ?? []).length === 0 ? (
                <Empty title="Операций ещё не было">
                  Здесь появятся ingest, query, lint и правки схемы — с тем, что каждая изменила.
                </Empty>
              ) : (
                <div className="timeline">
                  {(data.ops ?? []).map((op) => (
                    <OperationEntry key={op.op_id} op={op} />
                  ))}
                </div>
              )
            }
          </Loaded>
        </div>

        <div className="card">
          <div className="card__head">
            <h3>
              log.md <span className="path">{WIKI_LOG_PATH}</span>
            </h3>
            <div className="right">
              <span className="pill pill--accent pill--sm">пишет агент</span>
            </div>
          </div>
          <Loaded resource={log} what="журнал" skeletonRows={8}>
            {(data) =>
              data.content.trim().length === 0 ? (
                <Empty title="Журнал пуст">
                  Агент дописывает одну запись на операцию — после первого ingest здесь появится
                  хронология.
                </Empty>
              ) : (
                <pre className="logmd">{renderLog(data.content)}</pre>
              )
            }
          </Loaded>
          <hr className="hair" />
          <p className="field__hint" style={{ padding: '11px 15px', margin: 0 }}>
            Постоянный префикс даёт разбор обычным unix:{' '}
            <code>grep &quot;^## \[&quot; log.md | tail -5</code> — агент делает это внутри пода,
            чтобы понять, что происходило недавно.
          </p>
        </div>
      </div>
    </div>
  )
}

function OperationEntry({ op }: { op: HistoryOperation }) {
  const look = opLook(op.op)
  const status = statusLook(op)
  const changed = op.pages_created + op.pages_updated + op.pages_deleted

  return (
    <div className={`tl ${look.timelineClass}`}>
      <div className="tl__head">
        <span className="tl__op">{look.label}</span>
        <span className={`pill pill--sm ${status.pill}`}>{status.label}</span>
        {op.reverted_by_op_id ? (
          <span className="pill pill--neutral pill--sm">откачено</span>
        ) : null}
        <span className="tl__time">
          {dateTime(op.started_at)}
          {op.finished_at ? ` · ${duration(op.started_at, op.finished_at)}` : ''}
        </span>
      </div>
      <div className="tl__detail">{op.title || op.op_id}</div>
      <div className="tl__meta">
        <span>
          <b>{num(changed)}</b> файл.
        </span>
        {op.pages_created > 0 ? (
          <span>
            <b>+{num(op.pages_created)}</b> новых
          </span>
        ) : null}
        {op.pages_updated > 0 ? (
          <span>
            <b>~{num(op.pages_updated)}</b> обновлено
          </span>
        ) : null}
        {op.pages_deleted > 0 ? (
          <span>
            <b>−{num(op.pages_deleted)}</b> удалено
          </span>
        ) : null}
        {op.bytes_delta ? (
          <span>
            <b>{byteDelta(op.bytes_delta)}</b>
          </span>
        ) : null}
        <span>{authorLabel(op.author)}</span>
      </div>
    </div>
  )
}

/**
 * Highlight the `## [date] op | Title` headings and leave the rest verbatim.
 *
 * Not a markdown renderer: the value of this file to a reader is that it is the
 * plain text the agent wrote, so it is shown as such with only the headings marked.
 */
function renderLog(content: string) {
  return content.split('\n').map((line, index) => {
    const heading = /^##\s+\[/.test(line)
    return (
      <span key={index}>
        {heading ? <b>{line}</b> : line}
        {'\n'}
      </span>
    )
  })
}
